import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { DeployScope } from '@/deploy/DeployScope';
import { Dashboard as DeployDashboard } from '@/deploy/pages/Dashboard';
import { Deploy as DeployLauncher } from '@/deploy/pages/Deploy';
import { DeployProgress } from '@/deploy/pages/DeployProgress';
import { PromotionProgress as DeployPromotionProgress } from '@/deploy/pages/PromotionProgress';
import { Containers as DeployContainers } from '@/deploy/pages/Containers';
import { Environments as DeployEnvironments } from '@/deploy/pages/Environments';
import { History as DeployHistory } from '@/deploy/pages/History';
import { ApiServices as DeployApiServices } from '@/deploy/pages/ApiServices';
import { ResourcePacks as DeployResourcePacks } from '@/deploy/pages/ResourcePacks';
import { Database as DeployDatabase } from '@/deploy/pages/Database';
import { InstanceDetail as DeployInstanceDetail } from '@/deploy/pages/InstanceDetail';
import { Settings as DeploySettings } from '@/deploy/pages/Settings';
import { Logs as DeployLogs } from '@/deploy/pages/Logs';
import { DevEnvDetail as DeployDevEnvDetail } from '@/deploy/pages/DevEnvDetail';
import { BetaSlotDetail as DeployBetaSlotDetail } from '@/deploy/pages/BetaSlotDetail';
import { useDeployEnvStore } from '@/deploy/stores/deployEnv.store';
import { EMERGENCY_BASE, canonicalizeEmergencyUrl, emergencyCanonicalPath } from './emergencyPath';
import { EmergencyLayout } from './EmergencyLayout';

/** Page d'arrivée : les APIs, où l'on relance un serveur (MongoDB compris). */
const HOME = '/deploiement/apis';

/** Même redirection que le panel : l'env d'environnements mémorisé. */
function EmergencyEnvRedirect() {
  const env = useDeployEnvStore((s) => s.env);
  return <Navigate to={`/deploiement/environments/${env}`} replace />;
}

/**
 * Accès de secours à la section Déploiement, servi sous /secours.
 *
 * Routeur à part, de base /secours : les pages du Déploiement naviguent vers « /deploiement/... »
 * en dur, ce qui donne ici /secours/deploiement/... sans toucher à une seule page, et sans jamais
 * rejoindre les routes du panel. Seules les pages qui ne parlent qu'au CICD sont montées : pas de
 * « Demandes d'édit » (page du panel), et aucun store ni service du backend du panel.
 * La garde (connexion CICD obligatoire) est dans EmergencyLayout.
 */
export function EmergencyApp() {
  // Avant de monter le routeur : sa base /secours se compare au chemin brut (/%73ecours → /secours).
  canonicalizeEmergencyUrl();
  return (
    <BrowserRouter basename={EMERGENCY_BASE}>
      <Routes>
        <Route element={<EmergencyLayout />}>
          <Route index element={<Navigate to={HOME} replace />} />
          <Route path="deploiement" element={<DeployScope />}>
            <Route index element={<DeployDashboard />} />
            {/* La connexion est le formulaire du layout : connecté, rien à faire ici. */}
            <Route path="login" element={<Navigate to={HOME} replace />} />
            <Route path="deploy" element={<DeployLauncher />} />
            <Route path="deploy/progress" element={<DeployProgress />} />
            <Route path="deployments" element={<DeployPromotionProgress />} />
            <Route path="containers" element={<DeployContainers />} />
            <Route path="containers/:code" element={<DeployContainers />} />
            <Route path="environments" element={<EmergencyEnvRedirect />} />
            <Route path="environments/:envId" element={<DeployEnvironments />} />
            <Route path="environments/dev/:envId" element={<DeployDevEnvDetail />} />
            <Route path="environments/:envId/instance/:identifier" element={<DeployInstanceDetail />} />
            <Route path="environments/beta/:slotId" element={<DeployBetaSlotDetail />} />
            <Route path="history" element={<DeployHistory />} />
            <Route path="apis" element={<DeployApiServices />} />
            <Route path="resource-packs" element={<DeployResourcePacks />} />
            <Route path="database" element={<DeployDatabase />} />
            <Route path="logs" element={<DeployLogs />} />
            <Route path="settings" element={<DeploySettings />} />
            {/* « requests » et toute route inconnue : les APIs (pas de page du panel ici). */}
            <Route path="*" element={<Navigate to={HOME} replace />} />
          </Route>
          <Route path="*" element={<Navigate to={HOME} replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

/**
 * Route /secours/* du routeur du panel : n'est atteinte que par une navigation interne (le
 * chargement direct de /secours passe par EmergencyApp). On recharge la page pour que App
 * aiguille vers l'accès de secours, sans rien monter du panel. L'adresse rechargée est la forme
 * canonique (/secours littéral), qu'App reconnaît toujours : pas de boucle de rechargement.
 */
export function EmergencyReload() {
  useEffect(() => {
    const { pathname, search, hash } = window.location;
    window.location.replace((emergencyCanonicalPath(pathname) ?? EMERGENCY_BASE) + search + hash);
  }, []);
  return null;
}
